import DOMPurify from "dompurify";
import { ElementType, FC, ReactNode } from "react";

import __ from "web/Layout/Translations";

import Accordion from "web/Components/Common/Accordion";
import type { AccordionVariant } from "web/Components/Common/Accordion/accordion";
import HtmlTemplate from "web/Components/Common/HtmlTemplate";

import { Classes } from "web/types/Common";
import { Functionality } from "web/types/Employee";

import { useAppContext } from "web/context/app";
import {
  useGetEmployeeConfigurationQuery,
  useGetEmployeeDetailsQuery,
} from "web/features/employee/employeeApiSlice";

export interface IOrderDetailsLegalAccordionsProps {
  withdrawalContent?: string | null;
  withdrawalTemplateVariables?: {
    providerName: string;
    providerRegulations: string;
  };
  variant?: AccordionVariant;
  itemClassName?: string;
  accordionClassName?: string;
  contentClassName?: string;
  contentWrapper?: ElementType;
  accordionClasses?: Classes;
  groupClassName?: string;
  templateClassName?: string;
}

const OrderDetailsLegalAccordions: FC<IOrderDetailsLegalAccordionsProps> = ({
  withdrawalContent,
  withdrawalTemplateVariables,
  variant = "primary",
  itemClassName,
  accordionClassName,
  contentClassName,
  contentWrapper: ContentWrapper,
  accordionClasses,
  groupClassName,
  templateClassName,
}) => {
  const { isMobile } = useAppContext();
  const { data: employeeDetails } = useGetEmployeeDetailsQuery();
  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();

  const isRightOfWithdrawalEnabled = employeeDetails?.functionalities?.includes(
    Functionality.ENABLE_RIGHT_OF_WITHDRAWAL
  );

  const contractualContent =
    isRightOfWithdrawalEnabled && employeeConfig?.rightOfWithdrawal
      ? employeeConfig.rightOfWithdrawal
      : null;

  if (!withdrawalContent && !contractualContent) {
    return null;
  }

  const renderWithdrawalBody = () => {
    if (!withdrawalContent) {
      return null;
    }

    if (withdrawalTemplateVariables) {
      return (
        <HtmlTemplate
          template={withdrawalContent}
          variables={withdrawalTemplateVariables}
          className={templateClassName}
        />
      );
    }

    return (
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(withdrawalContent),
        }}
      />
    );
  };

  const wrapContent = (content: ReactNode) => {
    if (ContentWrapper) {
      return <ContentWrapper className={contentClassName}>{content}</ContentWrapper>;
    }

    if (contentClassName) {
      return <div className={contentClassName}>{content}</div>;
    }

    return content;
  };

  const wrapItem = (accordion: ReactNode) => {
    if (!isMobile && itemClassName) {
      return <div className={itemClassName}>{accordion}</div>;
    }

    return accordion;
  };

  const renderContractualBody = () => (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(contractualContent!),
      }}
    />
  );

  const withdrawalAccordion = withdrawalContent ? (
    <Accordion
      label={__("Odstąpienie od umowy")}
      arrow
      variant={variant}
      className={accordionClassName}
      classes={accordionClasses}
    >
      {wrapContent(renderWithdrawalBody())}
    </Accordion>
  ) : null;

  const contractualAccordion = contractualContent ? (
    <Accordion
      label={__("Umowne prawo anulacji")}
      arrow
      variant={variant}
      className={accordionClassName}
      classes={accordionClasses}
    >
      {wrapContent(renderContractualBody())}
    </Accordion>
  ) : null;

  const accordions = (
    <>
      {withdrawalAccordion ? wrapItem(withdrawalAccordion) : null}
      {contractualAccordion ? wrapItem(contractualAccordion) : null}
    </>
  );

  if (groupClassName) {
    return <div className={groupClassName}>{accordions}</div>;
  }

  return accordions;
};

export default OrderDetailsLegalAccordions;
